import React from 'react';
import { Paper, Typography, Box, LinearProgress, Tooltip, Chip, IconButton } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/currency';

// Gráfico de tendencia mensual
export const MonthlyTrendChart = ({ data, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: 300 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('monthly_trend') || 'Tendencia Mensual'}
          </Typography>
          <Tooltip 
            title={t('monthly_trend_tooltip') || 'Muestra la tendencia de ventas mensuales históricas. Se calcula desde los datos de ventas totales de cada mes, permitiendo visualizar la evolución del desempeño a lo largo del tiempo.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Cargando...</Typography>
        </Box>
      </Paper>
    );
  }

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const chartData = data?.map(item => ({
    month: `${monthNames[item.month - 1]} ${item.year}`,
    ventas: item.total,
    tickets: item.tickets
  })) || [];

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          {t('monthly_trend') || 'Tendencia Mensual'}
        </Typography>
        <Tooltip 
          title={t('monthly_trend_tooltip') || 'Muestra la tendencia de ventas mensuales históricas. Se calcula desde los datos de ventas totales de cada mes, permitiendo visualizar la evolución del desempeño a lo largo del tiempo.'}
          arrow
          placement="top"
        >
          <IconButton size="small" sx={{ p: 0.5 }}>
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <RechartsTooltip formatter={(value) => `${formatMoney(value)}`} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="ventas" 
            stroke="#1976d2" 
            strokeWidth={2}
            name="Ventas"
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

// Gráfico de ventas diarias
export const DailySalesChart = ({ data, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: 300 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('daily_sales') || 'Ventas Diarias (Últimos 7 días)'}
          </Typography>
          <Tooltip 
            title={t('daily_sales_tooltip') || 'Muestra las ventas diarias de los últimos 7 días del mes actual. Se calcula desde los datos de ventas diarias registradas, mostrando el total de ventas y tickets vendidos por día.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Cargando...</Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data?.map(item => ({
    día: `Día ${item.day}`,
    ventas: item.sales,
    tickets: item.tickets
  })) || [];

  if (!chartData || chartData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: 300 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('daily_sales') || 'Ventas Diarias (Últimos 7 días)'}
          </Typography>
          <Tooltip 
            title={t('daily_sales_tooltip') || 'Muestra las ventas diarias de los últimos 7 días del mes actual. Se calcula desde los datos de ventas diarias registradas, mostrando el total de ventas y tickets vendidos por día.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ 
          height: 250, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 1
        }}>
          <Typography variant="body1" color="text.secondary">
            {t('no_daily_sales_data') || 'No hay datos de ventas diarias disponibles'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('no_daily_sales_data_message') || 'Los datos de ventas diarias aparecerán aquí cuando estén disponibles'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          {t('daily_sales') || 'Ventas Diarias (Últimos 7 días)'}
        </Typography>
        <Tooltip 
          title={t('daily_sales_tooltip') || 'Muestra las ventas diarias de los últimos 7 días del mes actual. Se calcula desde los datos de ventas diarias registradas, mostrando el total de ventas y tickets vendidos por día.'}
          arrow
          placement="top"
        >
          <IconButton size="small" sx={{ p: 0.5 }}>
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="día" />
          <YAxis />
          <RechartsTooltip formatter={(value) => `${formatMoney(value)}`} />
          <Legend />
          <Bar dataKey="ventas" fill="#2e7d32" name="Ventas" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

// Tarjeta de objetivo mensual
export const MonthlyGoalCard = ({ objectives, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('monthly_goal') || 'Objetivo Mensual'}
          </Typography>
          <Tooltip 
            title={t('monthly_goal_tooltip') || 'Muestra el progreso hacia los objetivos mensuales asignados. Se calcula comparando las ventas reales del mes actual con los objetivos definidos por categoría (ventas, tickets, etc.).'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress />
      </Paper>
    );
  }

  if (!objectives || objectives.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('monthly_goal') || 'Objetivo Mensual'}
          </Typography>
          <Tooltip 
            title={t('monthly_goal_tooltip') || 'Muestra el progreso hacia los objetivos mensuales asignados. Se calcula comparando las ventas reales del mes actual con los objetivos definidos por categoría (ventas, tickets, etc.).'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('no_monthly_objectives') || 'No hay objetivos asignados para este mes'}
        </Typography>
      </Paper>
    );
  }

  console.log('[MonthlyGoalCard] Objectives received:', objectives);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          {t('monthly_goal') || 'Objetivo Mensual'}
        </Typography>
        <Tooltip 
          title={t('monthly_goal_tooltip') || 'Muestra el progreso hacia los objetivos mensuales asignados. Se calcula comparando las ventas reales del mes actual con los objetivos definidos por categoría (ventas, tickets, etc.).'}
          arrow
          placement="top"
        >
          <IconButton size="small" sx={{ p: 0.5 }}>
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {objectives.map((objective, index) => {
          const safeGoal = objective.targetValue ?? objective.target_value ?? 0;
          const safeCurrent = objective.actualValue ?? objective.actual_value ?? 0;
          const safeProgress = objective.progress ?? 0;
          const remaining = safeGoal - safeCurrent;
          
          // Determinar color según el progreso
          let progressColor = 'error'; // Naranja (0-50%)
          let backgroundColor = 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)';
          let borderColor = '#ff9800'; // Naranja
          let textColor = '#f57c00'; // Naranja oscuro
          
          if (safeProgress >= 75) {
            progressColor = 'success'; // Verde (75-100%)
            backgroundColor = 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)';
            borderColor = '#4caf50'; // Verde
            textColor = '#2e7d32'; // Verde oscuro
          } else if (safeProgress >= 50) {
            progressColor = 'warning'; // Amarillo (50-75%)
            backgroundColor = 'linear-gradient(135deg, #fffde7 0%, #fff9c4 100%)';
            borderColor = '#ffc107'; // Amarillo
            textColor = '#f57f17'; // Amarillo oscuro
          }

          return (
            <Box 
              key={objective.id || index}
              sx={{ 
                p: 2,
                borderRadius: 2,
                background: backgroundColor,
                border: '1px solid',
                borderColor: borderColor
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {objective.title || t('objective') || 'Objetivo'}
              </Typography>
              {objective.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {objective.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('current') || 'Actual'}: {safeCurrent.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('goal') || 'Objetivo'}: {safeGoal.toLocaleString()}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(safeProgress, 100)} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5, 
                  mb: 1,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progressColor === 'success' ? '#4caf50' : 
                                    progressColor === 'warning' ? '#ffc107' : '#ff9800'
                  }
                }}
                color={progressColor}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="h6" sx={{ color: textColor }}>
                  {safeProgress.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {remaining > 0 
                    ? `${t('remaining') || 'Faltan'}: ${remaining.toLocaleString()}`
                    : `${t('exceeded') || 'Superado'}! 🎉`
                  }
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// Tarjeta de ranking
export const RankingCard = ({ rank, totalSellers, teamAverage, currentTotal, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('ranking') || 'Tu Posición'}
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  const isTopThree = rank <= 3;
  const vsAverage = ((currentTotal - teamAverage) / teamAverage * 100).toFixed(1);

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3,
        background: isTopThree 
          ? 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)'
          : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t('ranking') || 'Tu Posición'}
      </Typography>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="h2" color={isTopThree ? 'warning.main' : 'primary.main'}>
          #{rank}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('of') || 'de'} {totalSellers} {t('sellers') || 'vendedores'}
        </Typography>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('vs_team_average') || 'vs Promedio del equipo'}
          </Typography>
          <Typography 
            variant="h6" 
            color={parseFloat(vsAverage) > 0 ? 'success.main' : 'error.main'}
            sx={{ mt: 0.5 }}
          >
            {parseFloat(vsAverage) > 0 ? '+' : ''}{vsAverage}%
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// Tarjeta de logros mejorada
export const AchievementsCard = ({ achievements, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('achievements') || 'Logros'}
          </Typography>
          <Tooltip 
            title={t('achievements_tooltip') || 'Muestra los logros disponibles y tu progreso hacia ellos. Se calcula en tiempo real basándose en tus métricas de ventas, tickets vendidos, nuevos clientes y otros indicadores definidos.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress />
      </Paper>
    );
  }

  if (!achievements || achievements.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('achievements') || 'Logros'}
          </Typography>
          <Tooltip 
            title={t('achievements_tooltip') || 'Muestra los logros disponibles y tu progreso hacia ellos. Se calcula en tiempo real basándose en tus métricas de ventas, tickets vendidos, nuevos clientes y otros indicadores definidos.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('no_achievements') || '¡Sigue trabajando para desbloquear logros!'}
        </Typography>
      </Paper>
    );
  }

  // Agrupar logros por categoría
  const achievementsByCategory = {
    sales: achievements.filter(a => a.category === 'sales'),
    tickets: achievements.filter(a => a.category === 'tickets'),
    growth: achievements.filter(a => a.category === 'growth'),
    average: achievements.filter(a => a.category === 'average'),
    clients: achievements.filter(a => a.category === 'clients'),
    special: achievements.filter(a => a.category === 'special')
  };

  const categoryLabels = {
    sales: t('sales_achievements') || 'Ventas',
    tickets: t('tickets_achievements') || 'Tickets',
    growth: t('growth_achievements') || 'Crecimiento',
    average: t('average_achievements') || 'Ticket Promedio',
    clients: t('clients_achievements') || 'Clientes',
    special: t('special_achievements') || 'Especiales'
  };

  // Calcular estadísticas
  const completedCount = achievements.filter(a => a.completed).length;
  const totalCount = achievements.length;
  const completedPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Agrupar logros completados por mes
  const achievementsByMonth = achievements
    .filter(a => a.completed && a.completedDate)
    .reduce((acc, achievement) => {
      const monthKey = dayjs(achievement.completedDate).format('YYYY-MM');
      const monthLabel = dayjs(achievement.completedDate).format('MMMM');
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          label: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
          count: 0,
          date: achievement.completedDate
        };
      }
      acc[monthKey].count++;
      return acc;
    }, {});

  // Convertir a array y ordenar por fecha (más reciente primero)
  const monthlyStats = Object.values(achievementsByMonth)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 6); // Últimos 6 meses

  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {t('achievements') || 'Logros'}
          </Typography>
          <Tooltip 
            title={t('achievements_tooltip') || 'Muestra los logros disponibles y tu progreso hacia ellos. Se calcula en tiempo real basándose en tus métricas de ventas, tickets vendidos, nuevos clientes y otros indicadores definidos.'}
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Box>
        <Chip 
          label={`${completedCount}/${totalCount}`}
          color="primary"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Box>
      
      {/* Barra de progreso general */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('progress') || 'Progreso General'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {completedPercentage}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={completedPercentage} 
          sx={{ height: 8, borderRadius: 1 }}
          color="primary"
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {(() => {
          const validCategories = Object.entries(achievementsByCategory).filter(([_, achievements]) => achievements.length > 0);
          return validCategories.map(([category, categoryAchievements], index) => {
            const isLast = index === validCategories.length - 1;
            
            return (
              <Box key={category} sx={{ mb: isLast ? 0 : 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                {categoryLabels[category]}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pl: 2 }}>
                {categoryAchievements.map((achievement) => (
                  <Tooltip
                    key={achievement.id}
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {achievement.description || achievement.name}
                        </Typography>
                        {achievement.completed && achievement.completedDate && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {t('completed_on') || 'Completado el'}: {dayjs(achievement.completedDate).format('DD/MM/YYYY')}
                          </Typography>
                        )}
                        {!achievement.completed && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {t('progress') || 'Progreso'}: {achievement.progress.toFixed(0)}%
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: achievement.completed ? 'success.light' : 'grey.100',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: achievement.completed ? 'success.main' : 'grey.300',
                        textAlign: 'center',
                        minWidth: 100,
                        opacity: achievement.completed ? 1 : 0.6,
                        position: 'relative',
                        transition: 'all 0.2s',
                        cursor: 'help',
                        '&:hover': {
                          transform: achievement.completed ? 'scale(1.05)' : 'scale(1.02)',
                          boxShadow: achievement.completed ? 2 : 1
                        }
                      }}
                    >
                      {achievement.completed && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -5,
                            right: -5,
                            bgcolor: 'success.main',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px'
                          }}
                        >
                          ✓
                        </Box>
                      )}
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          filter: achievement.completed ? 'none' : 'grayscale(100%)',
                          mb: 0.5
                        }}
                      >
                        {achievement.icon}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          fontWeight: achievement.completed ? 600 : 400,
                          color: achievement.completed ? 'text.primary' : 'text.secondary'
                        }}
                      >
                        {achievement.name}
                      </Typography>
                      {!achievement.completed && (
                        <Box sx={{ mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={achievement.progress}
                            sx={{ height: 4, borderRadius: 2 }}
                            color="primary"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {achievement.progress.toFixed(0)}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
            );
          });
        })()}
      </Box>
    </Paper>
  );
};

